import React, { ChangeEvent, useEffect, useState } from 'react';
import { stringify } from 'query-string';
import {
  append,
  equals,
  identity,
  ifElse,
  includes,
  isEmpty,
  map,
  mapObjIndexed,
  pick,
  pipe,
  without,
  toString,
  propOr,
  is,
  always,
  find,
  propEq,
  filter,
  isNil,
  complement,
  omit,
  all,
  values,
} from 'ramda';
import { isEqual } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { AnimatePresence } from 'framer-motion';

import { FilterItem, FilterOption } from '@definitions/filters.types';
import { DIRECTION_TYPES, SORT_TYPES } from '@definitions/artworks.types';
import {
  DEFAULT_FILTER_NAMES,
  FILTER_CONTAINER_TEST_ID,
  FILTER_NAME,
  FILTER_ORDER,
  MAX_PRICE,
  MIN_PRICE,
  SORT_ORDER,
} from '@components/headerFilter/headerFilter.constants';
import { ROUTE } from '@utils/constants';
import { useArtworksRoute } from '@hooks/useArtworks/useArtworks';
import { createArrayIfNot, mapArray } from '@utils/helpers';
import { SortFilterItem } from '@components/sortFilterItem';
import { BUTTON_SIZE } from '@components/form';
import { renderWhenTrue } from '@utils/rendering';
import { FocusTrap } from '@components/common';
import { InputField } from '@components/inputField';

import {
  ActionButton,
  Color,
  Container,
  FilterActions,
  FilterItemContainer,
  FilterItemMobileContainer,
  FilterMobileName,
  FilterOptionsContainer,
  FilterOptionsMobileContainer,
  FiltersContainer,
  FilterSpacer,
  Option,
  OptionColor,
  OptionText,
  TransformableContainer,
  DesktopFiltersContainer,
  MobileFiltersContainer,
  FilterName,
  FilterColorsContainer,
  PriceInputs,
} from './headerFilter.styles';

export interface HeaderFilterProps {
  options?: FilterItem[];
  open: boolean;
  onClose?: () => any;
  groupingSlug?: string;
}

interface SelectedOptions {
  inputPrice?: string;
  [key: string]: string | string[] | undefined;
}

type SelectedSort = {
  sort: SORT_TYPES;
  direction: DIRECTION_TYPES;
};

export const HeaderFilter = ({ options = [], open, onClose = () => ({}), groupingSlug }: HeaderFilterProps) => {
  const router = useRouter();
  const { sort: querySort, direction: queryDirection } = router.query as SelectedSort;
  const [sorting, setSorting] = useState<{ sort: SORT_TYPES | null; direction: DIRECTION_TYPES | null }>({
    sort: querySort || null,
    direction: queryDirection || null,
  });
  const handleOnClose = () => onClose();
  const { isGroupingRoute } = useArtworksRoute();
  const intl = useIntl();
  const closeLabel = intl.formatMessage({ id: 'headerFilter.close', defaultMessage: 'Close filter dropdown' });
  const minLabel = intl.formatMessage({ id: 'headerFilter.min', defaultMessage: 'Min' });
  const maxLabel = intl.formatMessage({ id: 'headerFilter.max', defaultMessage: 'Max' });
  const sanitizeOptions = pick(FILTER_ORDER);
  const sanitizeSort = pick(SORT_ORDER);
  const orderedOptions = filter(
    complement(isNil),
    map((filter) => find(propEq('name', filter), options || []), FILTER_ORDER)
  ) as FilterItem[];

  const [selectedOptions, setSelectedOptions] = useState<{
    inputPrice?: string;
    [key: string]: string | string[] | undefined;
  }>({});
  const [openOption, setOpenOption] = useState('');
  const isFilterOpen = equals(openOption);
  const hasParams = router.asPath?.includes('?');

  useEffect(() => {
    const { inputPrice } = router.query as { inputPrice: string };
    const queryOptions = mapObjIndexed(mapArray, sanitizeOptions(router.query));
    const sortOptions = mapObjIndexed(mapArray, sanitizeSort(router.query)) as SelectedSort;
    setSelectedOptions({ ...queryOptions, inputPrice });
    setSorting(sortOptions);
  }, []);

  const applyFilters = async () => {
    const { sort, direction } = sorting;
    const filterParams = filter(complement(isNil), { ...selectedOptions, sort, direction });
    const newQuery = stringify(filterParams, { arrayFormat: 'none', skipEmptyString: true });
    const newQueryParams = {
      pathname: router.pathname,
      query: filterParams,
    };

    const groupingSlug = router.query.grouping as string;
    const asPath = isGroupingRoute ? `${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${groupingSlug}?${newQuery}` : undefined;

    await router.push(newQueryParams, asPath, {
      shallow: true,
    });

    window.scrollTo(0, 0);

    onClose();
  };

  const handleFilterSelection = (filterItem: string, value: string) => {
    const addFilterOption = (options: SelectedOptions) => {
      const newOptionValue = append(value, createArrayIfNot(options[filterItem]));
      return { ...options, [filterItem]: newOptionValue };
    };
    const removeFilterOption = (options: SelectedOptions) => {
      const newOptionValue = without([value], createArrayIfNot(options[filterItem]));
      return { ...options, [filterItem]: newOptionValue };
    };

    const parseNewFilters =
      // @ts-ignore
      ifElse(
        // @ts-ignore
        () => includes(value, selectedOptions[filterItem]),
        removeFilterOption,
        addFilterOption
      );

    // @ts-ignore
    const newFilters: SelectedOptions = selectedOptions[filterItem]
      ? parseNewFilters(selectedOptions)
      : addFilterOption(selectedOptions);

    if (newFilters.price?.length) {
      setSelectedOptions(omit(['inputPrice'], newFilters));
      return;
    }

    setSelectedOptions(newFilters);
  };

  const handleFilterInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.currentTarget;
    const newOptions = { ...selectedOptions };
    if (!newOptions.inputPrice) {
      newOptions.inputPrice = name === MIN_PRICE ? `${value}_` : `_${value}`;
    }
    if (name === MIN_PRICE) {
      newOptions.inputPrice = newOptions.inputPrice.replace(/.*_/g, `${value}_`);
    }
    if (name === MAX_PRICE) {
      newOptions.inputPrice = newOptions.inputPrice.replace(/_.*/g, `_${value}`);
    }

    if (newOptions.inputPrice === '_') {
      setSelectedOptions(omit(['inputPrice'], newOptions));
      return;
    }

    setSelectedOptions(omit(['price'], newOptions));
  };

  const handleResetFilter = () => {
    setSelectedOptions({});
    setSorting({ sort: null, direction: null });
  };

  const clearUnappliedSelection = () => {
    const queryOptions = mapObjIndexed(mapArray, sanitizeOptions(router.query));
    const querySort = mapObjIndexed((value: string) => value, sanitizeSort(router.query)) as SelectedSort;
    const sort = isEqual(querySort, sorting) ? sorting : querySort;
    const options = isEqual(queryOptions, selectedOptions) ? selectedOptions : queryOptions;
    setSorting(sort);
    setSelectedOptions(options);
  };

  useEffect(() => {
    if (!open) clearUnappliedSelection();
  }, [open]);

  useEffect(() => {
    if (!hasParams && (!open || groupingSlug)) {
      handleResetFilter();
    }
  }, [open, groupingSlug]);

  const handleFilterClick = (name: string) => ifElse(isFilterOpen, () => setOpenOption(''), setOpenOption)(name);

  const renderDefaultFilter = (filterItem: string, option: FilterOption, index: number, parentIndex: number) => {
    const selectedFilters = pipe(propOr([], filterItem), map(ifElse(is(String), identity, toString)))(selectedOptions);
    const isSelected = includes(option.value.toString(), selectedFilters);

    return (
      <Option
        key={index}
        onClick={() => handleFilterSelection(filterItem, option.value)}
        autoFocus={!index && !parentIndex}
        aria-label={`Filter by ${option.name}`}
      >
        <OptionText selected={isSelected}>{option.name}</OptionText>
      </Option>
    );
  };

  const renderFilterOption = (filterItem: string, parentIndex: number) => (option: FilterOption, index: number) =>
    renderDefaultFilter(filterItem, option, index, parentIndex);

  const renderFilterOptions = (filterItem: FilterItem, parentIndex: number) => (
    <FilterOptionsContainer>
      {filterItem.options.map((option, index) => renderDefaultFilter(filterItem.name, option, index, parentIndex))}
      {renderPriceInputs(filterItem.name === 'price')}
    </FilterOptionsContainer>
  );

  const renderPriceInputs = renderWhenTrue(() => {
    const [minPrice, maxPrice] = selectedOptions.inputPrice ? selectedOptions.inputPrice.split('_') : ['', ''];

    return (
      <PriceInputs>
        <InputField
          name={MIN_PRICE}
          label={minLabel}
          type="number"
          pattern="[0-9]*"
          value={minPrice || ''}
          onChange={handleFilterInput}
        />
        <InputField
          name={MAX_PRICE}
          label={maxLabel}
          pattern="[0-9]*"
          type="number"
          value={maxPrice || ''}
          onChange={handleFilterInput}
        />
      </PriceInputs>
    );
  });

  const renderFilterItem = (filterItem: FilterItem, index: number) => {
    return (
      <FilterItemContainer key={index}>
        <FilterName>
          <FormattedMessage
            id={`filterName.${filterItem.name}`}
            defaultMessage={DEFAULT_FILTER_NAMES[filterItem.name]}
          />
          :
        </FilterName>
        {renderFilterOptions(filterItem, index)}
      </FilterItemContainer>
    );
  };

  const renderMobileFilterItem = (filterItem: FilterItem, index: number) => {
    return (
      <FilterItemMobileContainer key={index}>
        <FilterMobileName open={isFilterOpen(filterItem.name)} onClick={() => handleFilterClick(filterItem.name)}>
          <FormattedMessage
            id={`filterName.${filterItem.name}`}
            defaultMessage={DEFAULT_FILTER_NAMES[filterItem.name]}
          />
          :
        </FilterMobileName>
        <FilterOptionsMobileContainer open={isFilterOpen(filterItem.name)}>
          {filterItem.options.map(renderFilterOption(filterItem.name, index))}
          <FilterSpacer />
        </FilterOptionsMobileContainer>
      </FilterItemMobileContainer>
    );
  };

  const renderDesktopFilter = () => (
    <DesktopFiltersContainer>
      <FiltersContainer>
        <SortFilterItem onChangeSort={(sorting) => setSorting(sorting)} />
        {orderedOptions?.map(renderFilterItem)}
      </FiltersContainer>
    </DesktopFiltersContainer>
  );

  const renderMobileFilter = () => (
    <MobileFiltersContainer>
      <FiltersContainer>
        <SortFilterItem onChangeSort={(sorting) => setSorting(sorting)} />
        {orderedOptions?.map(renderMobileFilterItem)}
      </FiltersContainer>
    </MobileFiltersContainer>
  );

  const renderContent = renderWhenTrue(
    always(
      <TransformableContainer
        data-testid={FILTER_CONTAINER_TEST_ID}
        key="transformableContainer"
        initial={{ y: '-100%' }}
        animate={{ y: 0, transition: { duration: 0.6 } }}
        exit={{ y: '-100%', transition: { duration: 0.5 } }}
        transition={{ ease: 'ease-in-out' }}
      >
        <FocusTrap onFocus={handleOnClose} aria-label={closeLabel} />
        {renderDesktopFilter()}
        {renderMobileFilter()}
        <FilterActions>
          <ActionButton
            hide={all(isEmpty, values(selectedOptions)) && !sorting.sort}
            onClick={handleResetFilter}
            size={BUTTON_SIZE.M}
            fullWidth
          >
            <FormattedMessage id="headerFilter.reset" defaultMessage="Reset" />
          </ActionButton>
          <ActionButton onClick={applyFilters} size={BUTTON_SIZE.M} inverted fullWidth>
            <FormattedMessage id="headerFilter.apply" defaultMessage="Apply" />
          </ActionButton>
        </FilterActions>
        <FocusTrap onFocus={handleOnClose} />
      </TransformableContainer>
    )
  );

  return (
    <Container open={open}>
      <AnimatePresence>{renderContent(open)}</AnimatePresence>
    </Container>
  );
};
